import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { skeletonAccentProps, skeletonProps } from '../../styles/skeletonTheme';
import { useLanguage } from '../../contexts/LanguageContext';

function Bone(props) {
  return <Skeleton {...skeletonProps} {...props} />;
}

function AccentBone(props) {
  return <Skeleton {...skeletonAccentProps} {...props} />;
}

function LoadingAnnouncer() {
  const { t } = useLanguage();
  return <span className="sr-only">{t.common?.loadingContent || t.common?.loading || 'Loading'}</span>;
}

/** Full dashboard shell placeholder while routes/auth boot. */
export function FullPageSkeleton({ titleLines = 2, statCards = 4 }) {
  return (
    <div
      className="dashboard-shell app-shell min-h-screen"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingAnnouncer />
      <div className="container-premium section-padding space-y-8 px-4 sm:px-6 lg:px-8">
        <div className="card-premium p-6 sm:p-8">
          <Bone height={28} width="40%" />
          <div className="mt-4 space-y-2">
            {Array.from({ length: titleLines }).map((_, idx) => (
              <Bone
                key={`title-line-${idx}`}
                height={14}
                width={idx === 0 ? '70%' : '55%'}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-4">
          {Array.from({ length: statCards }).map((_, idx) => (
            <div key={`stat-skeleton-${idx}`} className="card-premium p-6">
              <div className="mb-4 flex items-center justify-between">
                <AccentBone circle height={44} width={44} />
                <Bone height={12} width={52} />
              </div>
              <Bone height={22} width="50%" />
              <div className="mt-3">
                <Bone height={12} width="75%" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:gap-8 xl:grid-cols-3">
          <div className="card-premium space-y-4 p-6 xl:col-span-2">
            <Bone height={22} width="35%" />
            {Array.from({ length: 4 }).map((_, idx) => (
              <div
                key={`list-skeleton-${idx}`}
                className="rounded-2xl border border-orange-100/40 p-4 "
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex w-full items-center gap-3">
                    <AccentBone circle height={40} width={40} />
                    <div className="w-full space-y-2">
                      <Bone height={14} width="38%" />
                      <Bone height={12} width="60%" />
                    </div>
                  </div>
                  <Bone height={12} width={72} />
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="card-premium space-y-3 p-6">
              <Bone height={18} width="55%" />
              {Array.from({ length: 4 }).map((_, idx) => (
                <Bone
                  key={`right-col-${idx}`}
                  height={12}
                  width={idx % 2 === 0 ? '95%' : '80%'}
                />
              ))}
            </div>
            <div className="card-premium space-y-3 p-6">
              <Bone height={18} width="45%" />
              <Bone height={40} width="100%" />
              <Bone height={40} width="100%" />
              <Bone height={40} width="100%" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** In-view list / table loading — replaces spinner panels. */
export function ListPageSkeleton({ rows = 6, showHeader = true, className = '' }) {
  return (
    <div
      className={`space-y-4 ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingAnnouncer />
      {showHeader ? (
        <div className="card-premium space-y-3 p-5 sm:p-6">
          <Bone height={22} width="32%" />
          <Bone height={12} width="55%" />
          <div className="flex flex-wrap gap-2 pt-2">
            <Bone height={36} width={120} borderRadius={999} />
            <Bone height={36} width={160} borderRadius={999} />
          </div>
        </div>
      ) : null}
      <div className="card-premium overflow-hidden p-2 sm:p-3">
        {Array.from({ length: rows }).map((_, idx) => (
          <div
            key={`row-${idx}`}
            className="flex items-center gap-3 border-b border-orange-100/30 px-3 py-3.5 last:border-0 "
          >
            <AccentBone circle height={36} width={36} />
            <div className="min-w-0 flex-1 space-y-2">
              <Bone height={13} width={`${48 + (idx % 3) * 12}%`} />
              <Bone height={11} width={`${62 + (idx % 4) * 8}%`} />
            </div>
            <Bone height={28} width={72} borderRadius={999} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compact card-grid placeholder (rewards, promo cards, etc.). */
export function CardGridSkeleton({ cards = 4, className = '' }) {
  return (
    <div
      className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingAnnouncer />
      {Array.from({ length: cards }).map((_, idx) => (
        <div key={`card-${idx}`} className="card-premium space-y-4 p-6">
          <div className="flex items-center gap-3">
            <AccentBone circle height={48} width={48} />
            <div className="flex-1 space-y-2">
              <Bone height={14} width="50%" />
              <Bone height={12} width="70%" />
            </div>
          </div>
          <Bone height={120} width="100%" borderRadius={16} />
          <Bone height={36} width="100%" borderRadius={12} />
        </div>
      ))}
    </div>
  );
}

/** Stat cards row (analytics / platform overview). */
export function StatsRowSkeleton({ count = 4 }) {
  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingAnnouncer />
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`stats-row-${idx}`} className="card-premium p-6">
          <div className="mb-4 flex items-center justify-between">
            <AccentBone circle height={40} width={40} />
            <Bone height={12} width={48} />
          </div>
          <Bone height={24} width="45%" />
          <div className="mt-3">
            <Bone height={12} width="70%" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Inline / route boot placeholder.
 * Renders skeleton bones (not a spinner). Kept name for call-site compatibility.
 */
export function CenteredLoaderSkeleton({ className = '', compact = false }) {
  if (compact) {
    return <ListPageSkeleton rows={4} showHeader={false} className={className} />;
  }

  return (
    <div
      className={`dashboard-shell flex min-h-screen items-center justify-center px-4 py-10 ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <LoadingAnnouncer />
      <div className="w-full max-w-lg space-y-4">
        <div className="card-premium space-y-4 p-8">
          <div className="flex justify-center">
            <AccentBone circle height={56} width={56} />
          </div>
          <Bone height={20} width="48%" className="mx-auto" />
          <Bone height={12} width="72%" className="mx-auto" />
          <div className="space-y-3 pt-2">
            <Bone height={40} width="100%" borderRadius={12} />
            <Bone height={40} width="100%" borderRadius={12} />
            <Bone height={40} width="85%" borderRadius={12} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function RewardsViewSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-live="polite" aria-busy="true">
      <LoadingAnnouncer />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="card-premium space-y-4 p-6 md:col-span-5">
          <Bone height={20} width="45%" />
          <Bone height={40} width="35%" />
          <Bone height={12} width="60%" />
        </div>
        <div className="card-premium space-y-3 p-6 md:col-span-7">
          <Bone height={20} width="40%" />
          <AccentBone height={8} width="100%" borderRadius={999} />
          <Bone height={12} width="50%" />
        </div>
      </div>
      <div className="card-premium p-6">
        <Bone height={22} width="30%" className="mb-6" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div
              key={`reward-card-${idx}`}
              className="overflow-hidden rounded-2xl border border-orange-100/50 "
            >
              <AccentBone height={140} width="100%" borderRadius={0} />
              <div className="space-y-2 p-5">
                <Bone height={12} width="90%" />
                <Bone height={12} width="55%" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
